
import { mergeDateTime } from '@utils/Date';
import { parseEmbedUrl } from '@utils/Embed';
import { isAfter, isFuture } from 'date-fns';
import * as z from 'zod';

export const DEFAULT_OPTION = {
  title: '',
  description: '',
};

export const DEFAULT_QUESTION = {
  question: '',
  options: [{ ...DEFAULT_OPTION }, { ...DEFAULT_OPTION }],
};

export const DEFAULT_IMAGES = {
  options: [undefined, undefined],
};

export const QUESTION_SCHEMA = z.object({
  subPollId: z.string().optional(),
  question: z.string().min(1, 'must not be empty').max(80),
  isNotExact:z.boolean().optional(),
  maxVote: z.any().optional(),
  options: z
    .object({
      title: z.string().min(1, 'must not be empty').max(80),
      description: z
        .string()
        .max(80, 'Maximum length is 80 characters')
        .optional(),
      embed: z
        .string()
        .refine((v) => {
          if (v) return parseEmbedUrl(v);
          return true;
        }, 'Invalid media url')
        .optional(),
      price: z.string().optional(),
      currency: z.string().optional(),
      image: z.any().optional(),
      removeImage: z.boolean().optional(),
    })
    .array()
    .min(1)
    .max(20),
});

export const CREATE_POLL_SCHEMA = z
  .object({
    isEdit: z.boolean().optional(),
    private: z.string(),
    company: z.boolean().optional(),
    toPledge: z.boolean().optional(),
    category:z.string().optional(),
    hashtag:z.any().optional(),
    password: z.string().min(1).optional().nullable(),
    image: z.any().optional(),
    grc: z.string().optional(),
    isNotExact:z.boolean().optional(),
    division: z.string().optional(),
    isPremium: z.boolean().optional(),
    title: z.string().min(1, 'must not be empty').max(120),
    period: z.object({
      immediate: z.boolean(),
      focusedInput: z.enum(['startDate', 'endDate']).nullable(),
      startDate: z.date(),
      endDate: z.date(),
      startTime: z.date(),
      endTime: z.date(),
    }),
    description: z
      .string()
      .max(1000, 'Maximum length is 1000 characters')
      .optional(),
    video: z
      .string()
      .refine((v) => {
        if (v) return parseEmbedUrl(v);
        return true;
      }, 'Invalid media url')
      .optional(),
    group: z.boolean(),
    pledging: z.boolean(),
    themeID: z.string().optional(),
    pledgePeriod: z.object({
      immediate: z.boolean(),
      focusedInput: z.enum(['startDate', 'endDate']).nullable(),
      startDate: z.date(),
      endDate: z.date(),
      startTime: z.date(),
      endTime: z.date(),
    }),
    questions: QUESTION_SCHEMA.array().min(1).max(10),
    maxVote: z.any().optional(),
    deletedQuestions: z
      .object({
        subPollId: z.string().optional(),
      })
      .array()
      .optional(),
  })
  .refine(
    (data) => {
      if (data.isEdit || data.period.immediate) return true;
      const start = mergeDateTime(data.period.startDate, data.period.startTime);
      return isFuture(start) || data.toPledge;
    },
    {
      message: 'Poll start must be in the future',
      path: ['period.startDate'],
    }
  )
  .refine(
    (data) => {
      if (data.isEdit) return true;
      const end = mergeDateTime(data.period.endDate, data.period.endTime);
      console.log(data.toPledge);
      return isFuture(end) || data.toPledge;
    },
    {
      message: 'Poll end must be in the future',
      path: ['period.endDate'],
    }
  )
  .refine(
    (data) => {
      const start = mergeDateTime(
        data.period.startDate,
        data.period.immediate ? new Date() : data.period.startTime
      );
      const end = mergeDateTime(data.period.endDate, data.period.endTime);
      return isAfter(end, start) || data.toPledge;
    },
    {
      message: 'Poll end must be after start',
      path: ['period.endDate'],
    }
  )
  .refine(
    (data) => {
      if (data.pledging === false) return true;
      const end = mergeDateTime(data.period.endDate, data.period.endTime);
      const pledgeStart = mergeDateTime(
        data.pledgePeriod.startDate,
        data.pledgePeriod.startTime
      );
      return isAfter(pledgeStart, end);
    },
    {
      message: 'Pledge must start after vote period',
      path: ['pledgeStart.startDate'],
    }
  )
  .refine(
    (data) => {
      if (data.pledging === false) return true;
      const pledgeStart = mergeDateTime(
        data.pledgePeriod.startDate,
        data.pledgePeriod.startTime
      );
      const pledgeEnd = mergeDateTime(
        data.pledgePeriod.endDate,
        data.pledgePeriod.endTime
      );
      return isAfter(pledgeEnd, pledgeStart);
    },
    {
      message: 'Pledge end must be after start',
      path: ['pledgeStart.endDate'],
    }
  )
  .refine(
    (data) => {
      if (data.pledging === false) return true;
      let validPrice = true;
      data.questions?.[0]?.options?.forEach((opt) => {
        const price = opt.price?.replace('$', '');
        if (price === undefined) {
          validPrice = false;
        } else if (isNaN(parseFloat(price))) {
          validPrice = false;
        }
      });

      return validPrice;
    },
    {
      message: 'must have a price if pledging is enabled',
      path: ['questions'],
    }
  );

export type CreatePollData = z.infer<typeof CREATE_POLL_SCHEMA>;
